'use client';

import { useState } from 'react';
import { X, Lock, Mail, ShieldCheck, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

type LoginMode = 'LOGIN' | 'REGISTER' | '2FA';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLoginSuccess: (token: string, user: any) => void;
}

export default function LoginModal({ isOpen, onClose, onLoginSuccess }: LoginModalProps) {
    const { lang } = useLanguage();
    const [mode, setMode] = useState<LoginMode>('LOGIN');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [tempUserId, setTempUserId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            if (mode === 'LOGIN') {
                const { data } = await axios.post('/auth/login', { email, password });

                if (data.requires2FA) {
                    setTempUserId(data.userId);
                    setMode('2FA');
                    setError('');
                    return;
                }

                if (data.access_token) {
                    onLoginSuccess(data.access_token, data.user);
                    onClose();
                } else {
                    setError(lang === 'FR' ? 'Erreur inconnue lors de la connexion.' : 'Unknown login error.');
                }
            } else if (mode === 'REGISTER') {
                await axios.post('/auth/register', { email, password });
                // After successful registration, attempt to log in
                const { data } = await axios.post('/auth/login', { email, password });
                if (data.access_token) {
                    onLoginSuccess(data.access_token, data.user);
                    onClose();
                } else {
                    setError(lang === 'FR' ? 'Compte créé ! Veuillez vous connecter.' : 'Account created! Please log in.');
                    setMode('LOGIN'); // Switch to login mode after successful registration
                }
            } else if (mode === '2FA') {
                const { data } = await axios.post('/auth/login-2fa', { userId: tempUserId, twoFactorCode });
                if (data.access_token) {
                    onLoginSuccess(data.access_token, data.user);
                    onClose();
                } else {
                    setError(lang === 'FR' ? 'Code 2FA invalide.' : 'Invalid 2FA code.');
                }
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Une erreur est survenue.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-zinc-950 rounded-[2rem] p-8 w-full max-w-sm shadow-2xl shadow-white/5 border border-white/10 relative overflow-hidden">
                <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                </button>

                <div className="text-center mb-8">
                    <div className="inline-flex p-3 bg-white/5 border border-white/10 rounded-full mb-4 text-white">
                        {mode === 'LOGIN' && <Lock className="w-6 h-6" />}
                        {mode === 'REGISTER' && <Mail className="w-6 h-6" />}
                        {mode === '2FA' && <ShieldCheck className="w-6 h-6" />}
                    </div>
                    <h2 className="text-2xl font-bold text-white tracking-tight">
                        {mode === 'LOGIN' ? (lang === 'FR' ? 'Connexion' : 'Login') : mode === 'REGISTER' ? (lang === 'FR' ? 'Création de compte' : 'Create Account') : (lang === 'FR' ? 'Sécurité Renforcée' : 'Enhanced Security')}
                    </h2>
                    <p className="text-xs text-zinc-400 mt-2 uppercase tracking-widest font-semibold flex flex-col gap-1">
                        {mode === 'LOGIN' ? (lang === 'FR' ? 'Accès Privé' : 'Private Access') : mode === 'REGISTER' ? (lang === 'FR' ? 'Rejoignez Ootentik' : 'Join Ootentik') : (lang === 'FR' ? 'Vérification Requise' : 'Verification Required')}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === '2FA' ? (
                        <div>
                            <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 ml-1">{lang === 'FR' ? 'Code 2FA' : '2FA Code'}</label>
                            <div className="relative">
                                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                                <input
                                    type="text"
                                    maxLength={6}
                                    required
                                    value={twoFactorCode}
                                    onChange={(e) => setTwoFactorCode(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-white/10 rounded-xl leading-5 bg-black text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-white focus:border-white transition-all shadow-inner"
                                    placeholder="000000"
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 ml-1">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-3 border border-white/10 rounded-xl leading-5 bg-black text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-white focus:border-white transition-all shadow-inner"
                                        placeholder="contact@ootentik.com"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 ml-1">{lang === 'FR' ? 'Mot de passe' : 'Password'}</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                                    <input
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-3 border border-white/10 rounded-xl leading-5 bg-black text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-white focus:border-white transition-all shadow-inner"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {error && (
                        <div className="p-3 bg-red-950 text-red-400 border border-red-900/50 text-xs rounded-lg text-center font-medium tracking-wide">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-4 mt-2 px-4 shadow-[0_0_20px_rgba(255,255,255,0.1)] rounded-xl text-xs uppercase tracking-widest font-bold text-black bg-white hover:bg-zinc-200 focus:outline-none disabled:opacity-50 disabled:shadow-none flex items-center justify-center gap-2 transition-all"
                    >
                        {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isLoading ? (lang === 'FR' ? 'TRAITEMENT...' : 'PROCESSING...') : mode === 'LOGIN' ? (lang === 'FR' ? 'S\'IDENTIFIER' : 'LOG IN') : mode === 'REGISTER' ? (lang === 'FR' ? 'S\'INSCRIRE' : 'SIGN UP') : (lang === 'FR' ? 'VÉRIFIER LE CODE' : 'VERIFY CODE')}
                    </button>
                </form>

                <div className="mt-8 text-center pt-6 border-t border-white/5">
                    {mode !== '2FA' && (
                        <button
                            onClick={() => { setMode(mode === 'LOGIN' ? 'REGISTER' : 'LOGIN'); setError(''); }}
                            className="text-[10px] text-zinc-500 hover:text-white font-bold tracking-widest uppercase transition-colors"
                        >
                            {mode === 'REGISTER' ? (lang === 'FR' ? 'Retour à la connexion' : 'Back to login') : (lang === 'FR' ? 'Besoin d\'un accès ? S\'inscrire' : 'Need access? Sign up')}
                        </button>
                    )}
                    {mode === '2FA' && (
                        <button
                            onClick={() => { setMode('LOGIN'); setTwoFactorCode(''); setError(''); }}
                            className="text-[10px] text-zinc-500 hover:text-white font-bold tracking-widest uppercase transition-colors"
                        >
                            {lang === 'FR' ? 'Abandonner l\'identification' : 'Cancel authentication'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
